
import Vue from "vue";
import {
  getPartnerAffiliation,
  setPartnerAffiliation,
  removePartnerAffiliation,
  previewPartnerAffiliation,
  PartnerAffiliationStatus,
  PartnerAffiliationPreview,
  PartnerAffiliationResult
} from "@/api";

export default Vue.extend({
  name: "PartnerAffiliation",
  data() {
    return {
      partnerOptions: [
        { label: "123Dentist", value: 2 },
        { label: "DentalCorp", value: 1 }
      ],

      // Set form
      setFormValid: false,
      setLoading: false,
      setLocationId: null as number | null,
      setPartner: null as number | null,
      setCentralBookedId: "",
      setMessage: "",
      setMessageType: "success" as "success" | "error",
      reviewOpen: false,
      conversionPreview: null as PartnerAffiliationPreview | null,
      conversionResult: null as PartnerAffiliationResult | null,
      pendingAffiliation: null as {
        locationId: number;
        centralBookedId: string;
        previewToken: string;
        idempotencyKey: string;
      } | null,
      confirmError: "",

      // Lookup
      lookupLoading: false,
      lookupLocationId: null as number | null,
      lookupResult: null as PartnerAffiliationStatus | null,
      lookupError: "",

      // Remove
      confirmRemoveDialog: false,
      removeLoading: false,
      removeMessage: "",
      removeMessageType: "success" as "success" | "error",

      rules: {
        required: (v: any) => !!v || "This field is required",
        positiveInt: (v: any) =>
          (Number.isInteger(Number(v)) && Number(v) > 0) ||
          "Must be a positive integer",
        maxNineChars: (v: string) =>
          !v ||
          v.length <= 9 ||
          "Central Booking ID must be 9 characters or fewer"
      }
    };
  },
  watch: {
    setPartner() {
      this.setCentralBookedId = "";
    }
  },
  methods: {
    async submitSetPartner() {
      const form = this.$refs.setForm as any;
      if (!form.validate()) return;

      this.setLoading = true;
      this.setMessage = "";
      this.conversionResult = null;
      try {
        const payload: {
          locationId: number;
          partner: 1 | 2;
          centralBookedId?: string;
        } = {
          locationId: this.setLocationId!,
          partner: this.setPartner as 1 | 2
        };
        if (this.setPartner === 2) {
          payload.centralBookedId = this.setCentralBookedId.trim();
          this.conversionPreview = await previewPartnerAffiliation({
            locationId: payload.locationId,
            centralBookedId: payload.centralBookedId
          });
          const bytes = new Uint8Array(16);
          window.crypto.getRandomValues(bytes);
          this.pendingAffiliation = {
            locationId: payload.locationId,
            centralBookedId: payload.centralBookedId,
            previewToken: this.conversionPreview.previewToken,
            idempotencyKey:
              "partner-affiliation:" +
              Array.from(bytes)
                .map(n => n.toString(16).padStart(2, "0"))
                .join("")
          };
          this.confirmError = "";
          this.reviewOpen = true;
          return;
        }
        const result = await setPartnerAffiliation(payload);
        this.setMessage = result.message;
        this.setMessageType = "success";
        if (result.location && this.lookupResult?.id === result.location.id) {
          this.lookupResult = result.location;
          this.removeMessage = "";
        }
        form.reset();
      } catch (err) {
        this.setMessage = (err as Error).message || "An error occurred";
        this.setMessageType = "error";
      } finally {
        this.setLoading = false;
      }
    },

    cancelReview() {
      if (this.setLoading) return;
      this.reviewOpen = false;
      this.pendingAffiliation = null;
      this.conversionPreview = null;
      this.confirmError = "";
    },

    async confirmAffiliation() {
      if (!this.pendingAffiliation || this.setLoading) return;
      this.setLoading = true;
      this.confirmError = "";
      try {
        const result = await setPartnerAffiliation({
          partner: 2,
          ...this.pendingAffiliation
        });
        this.conversionResult = result;
        this.setMessage = result.message;
        this.setMessageType = "success";
        if (result.location && this.lookupResult?.id === result.location.id) {
          this.lookupResult = result.location;
          this.removeMessage = "";
        }
        this.reviewOpen = false;
        this.pendingAffiliation = null;
        (this.$refs.setForm as any).reset();
      } catch (error) {
        const err = error as Error & { status?: number };
        this.confirmError =
          err.message || "Unable to confirm. Retry this same confirmation.";
        if (err.status === 409) {
          this.setMessage = this.confirmError;
          this.setMessageType = "error";
          this.reviewOpen = false;
          this.pendingAffiliation = null;
        }
      } finally {
        this.setLoading = false;
      }
    },

    async lookupPartner() {
      if (!this.lookupLocationId) return;
      this.lookupLoading = true;
      this.lookupError = "";
      this.lookupResult = null;
      this.removeMessage = "";
      this.conversionResult = null;
      try {
        this.lookupResult = await getPartnerAffiliation(this.lookupLocationId);
      } catch (err) {
        this.lookupError = (err as Error).message || "An error occurred";
      } finally {
        this.lookupLoading = false;
      }
    },

    async executeRemove() {
      if (!this.lookupResult) return;
      this.removeLoading = true;
      this.conversionResult = null;
      try {
        const result = await removePartnerAffiliation(this.lookupResult.id);
        this.confirmRemoveDialog = false;
        this.removeMessage = result.message;
        this.removeMessageType = "success";
        // Refresh the lookup result
        this.lookupResult = await getPartnerAffiliation(this.lookupResult.id);
      } catch (err) {
        this.confirmRemoveDialog = false;
        this.removeMessage = (err as Error).message || "An error occurred";
        this.removeMessageType = "error";
      } finally {
        this.removeLoading = false;
      }
    }
  }
});
